import {
  AlertCircle,
  BookOpen,
  Calculator,
  FileText,
  Lightbulb,
  Zap,
} from 'lucide-react';
import { memo } from 'react';

import type { ConceptSidebarContent } from '../layouts/math-concept';

import { renderTextWithMathAndHTML } from '../utils/math-text-renderer';

interface ConceptSidebarProps {
  breadcrumbTitle: string;
  sidebarContent: ConceptSidebarContent;
}

const ConceptSidebar = memo(
  ({ breadcrumbTitle, sidebarContent }: ConceptSidebarProps) => {
    const renderSidebarHeader = () => (
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <BookOpen aria-hidden="true" className="w-6 h-6 text-brand-primary" />
          <div className="flex flex-col">
            <h2 className="text-xl font-bold text-primary">
              {breadcrumbTitle}
            </h2>
            <span className="text-sm text-secondary">Study Guide</span>
          </div>
        </div>
      </div>
    );

    const renderTheoremSection = () => {
      if (!sidebarContent.theorem) return null;

      return (
        <div className="explanation-box-semantic bg-semantic-warning/15 border border-semantic-warning/30 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb aria-hidden="true" className="w-4 h-4 text-semantic-warning" />
            <h3 className="font-semibold text-primary text-base">
              {sidebarContent.theorem.title}
            </h3>
          </div>
          <div className="text-sm text-secondary">
            {renderTextWithMathAndHTML(sidebarContent.theorem.content)}
          </div>
        </div>
      );
    };

    const renderNotesSection = () => {
      if (!sidebarContent.notes) return null;

      return (
        <div className="explanation-box-semantic bg-semantic-error/15 border border-semantic-error/30 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle aria-hidden="true" className="w-4 h-4 text-semantic-error" />
            <h3 className="font-semibold text-primary text-base">
              {sidebarContent.notes.title}
            </h3>
          </div>
          <ul className="space-y-1 text-sm text-secondary">
            {sidebarContent.notes.items.map((item, index) => (
              <li className="flex items-start gap-2" key={index}>
                <span aria-hidden="true" className="text-semantic-error mt-1">•</span>
                <span>{renderTextWithMathAndHTML(item)}</span>
              </li>
            ))}
          </ul>
        </div>
      );
    };

    const renderNotationSection = () => {
      if (!sidebarContent.notation) return null;

      return (
        <div className="explanation-box-semantic bg-brand-primary/15 border border-brand-primary/30 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <FileText aria-hidden="true" className="w-4 h-4 text-brand-primary" />
            <h3 className="font-semibold text-primary text-base">
              {sidebarContent.notation.title}
            </h3>
          </div>
          <div className="space-y-2 text-sm text-secondary">
            {sidebarContent.notation.content.map((line, index) => (
              <div className="font-mono text-xs" key={index}>
                {renderTextWithMathAndHTML(line)}
              </div>
            ))}
            {sidebarContent.notation.note && (
              <div className="mt-2 text-xs text-tertiary italic">
                {renderTextWithMathAndHTML(sidebarContent.notation.note)}
              </div>
            )}
          </div>
        </div>
      );
    };

    const renderWhyItWorksSection = () => {
      if (!sidebarContent.whyItWorks) return null;

      return (
        <div className="explanation-box-semantic bg-semantic-success/15 border border-semantic-success/30 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-semantic-success" />
            <h3 className="font-semibold text-primary text-base">
              {sidebarContent.whyItWorks.title}
            </h3>
          </div>
          <div className="text-sm text-secondary">
            {renderTextWithMathAndHTML(sidebarContent.whyItWorks.content)}
          </div>
        </div>
      );
    };

    const renderRememberSection = () => {
      if (!sidebarContent.remember) return null;

      return (
        <div className="explanation-box-semantic bg-semantic-info/15 border border-semantic-info/30 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-4 h-4 text-semantic-info" />
            <h3 className="font-semibold text-primary text-base">
              {sidebarContent.remember.title}
            </h3>
          </div>
          <div className="text-sm text-secondary">
            {renderTextWithMathAndHTML(sidebarContent.remember.content)}
          </div>
        </div>
      );
    };

    const renderQuickReferenceSection = () => {
      if (!sidebarContent.quickReference) return null;

      return (
        <div className="surface-card rounded-xl shadow-lg border border-subtle overflow-hidden">
          <div className="h-2 gradient-primary" />
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-brand-secondary" />
                <h3 className="text-xl font-bold text-primary">
                  {sidebarContent.quickReference.title}
                </h3>
              </div>
            </div>
            <div className="space-y-3">
              {sidebarContent.quickReference.items.map((item, index) => (
                <div className="grid grid-cols-[1fr_auto] gap-10 text-sm items-start" key={index}>
                  <span className="text-secondary font-medium">
                    {renderTextWithMathAndHTML(item.label)}:
                  </span>
                  <span className="text-primary font-bold text-left">
                    {renderTextWithMathAndHTML(item.value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    };

    return (
      <aside aria-label="Study guide and reference materials" className="space-y-6">
        <div className="surface-card rounded-xl shadow-lg border border-subtle overflow-hidden">
          <div className="h-2 gradient-primary" />
          <div className="p-6">
            {renderSidebarHeader()}

            <div className="space-y-6">
              {renderTheoremSection()}
              {renderNotesSection()}
              {renderNotationSection()}
              {renderWhyItWorksSection()}
              {renderRememberSection()}
            </div>
          </div>
        </div>

        {renderQuickReferenceSection()}
      </aside>
    );
  }
);

ConceptSidebar.displayName = 'ConceptSidebar';

export default ConceptSidebar;
